import {
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Option,
  Separator,
  showNotification,
} from 'platform/components';
import {Grid, GridItem, HStack, VStack} from 'platform/foundation';

import {useEffect, useState} from 'react';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {CurrencyResponseBody} from '@dms/api/shared';
import {
  BaseSupplier,
  BaseSupplierOrder,
  GetSupplierOrderVariantsResponse,
  GetWarehouseResponse,
  GetWarehousesResponse,
  PatchSupplierOrderRequest,
  useLazyGetWarehouseQuery,
  usePatchSupplierOrderMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  getOptionsFromCurrencies,
  getOptionsFromSuppliers,
  getOptionsFromWarehouses,
  handleApiError,
} from '@dms/shared';
import {getOptionsFromSupplierOrderVariant} from '@dms/warehouse/shared';

import {Nullish, TestIdProps, suffixTestId} from 'shared/web';

import {SupplierOrderForm, SupplierOrderFormSchema} from '../types/SupplierOrderForm';

interface BasicInformationFormProps extends TestIdProps {
  supplierOrder: BaseSupplierOrder | Nullish;
  warehouses: GetWarehousesResponse | Nullish;
  suppliers: BaseSupplier[] | Nullish;
  currencies: CurrencyResponseBody[] | Nullish;
  supplierOrderTypes: Option[] | Nullish;
  supplierAccounts: Option[] | Nullish;
  supplierOrderVariants: GetSupplierOrderVariantsResponse[] | Nullish;
  onSetReadonly: (isReadOnly: boolean) => void;
}

export function BasicInformationForm(props: BasicInformationFormProps) {
  const [selectedWarehouse, setSelectedWarehouse] = useState<GetWarehouseResponse | Nullish>(null);

  const [patchSupplierOrder] = usePatchSupplierOrderMutation();
  const [getWarehouse, {isFetching: isWarehouseFetching}] = useLazyGetWarehouseQuery();

  useEffect(
    function getWarehouseFromSupplierOrder() {
      if (isNilOrEmpty(props.supplierOrder?.warehouseId)) {
        return;
      }

      getWarehouse({warehouseId: props.supplierOrder?.warehouseId as string})
        .unwrap()
        .then(setSelectedWarehouse)
        .catch(handleApiError);
    },
    [props.supplierOrder?.warehouseId, getWarehouse]
  );

  // When the supplier has exactly one account the field is disabled (see below), so fall back
  // to auto-selecting it for orders that have none saved yet - otherwise the only available
  // account could never be persisted on save.
  const defaultSupplierAccountCode =
    props.supplierOrder?.supplierAccountCode ??
    (props.supplierAccounts?.length === 1 ? props.supplierAccounts[0].value : undefined);

  const defaultValues: Partial<SupplierOrderForm> = {
    warehouseId: props.supplierOrder?.warehouseId,
    supplierId: props.supplierOrder?.supplierId,
    orderVariantId: props.supplierOrder?.orderVariantId,
    type: props.supplierOrder?.type,
    supplierAccountCode: defaultSupplierAccountCode,
    currency: props.supplierOrder?.currency,
    exchangeRate: props.supplierOrder?.exchangeRate,
    ncConversion: props.supplierOrder?.ncConversion,
    note: props.supplierOrder?.note,
  };

  const handleDiscard = () => {
    props.onSetReadonly(true);
  };

  const handleSubmit: FormSubmitHandler<SupplierOrderForm> = async (data) => {
    const body: PatchSupplierOrderRequest['body'] = {
      warehouseId: data.warehouseId,
      supplierId: data.supplierId,
      orderVariantId: data.orderVariantId,
      type: data.type,
      supplierAccountCode: data.supplierAccountCode,
      currency: data.currency,
      exchangeRate: data.exchangeRate,
      ncConversion: data.ncConversion,
      orderExternalNumber: props.supplierOrder?.orderExternalNumber,
      referenceNumber: props.supplierOrder?.referenceNumber,
      note: data.note,
    };

    return await patchSupplierOrder({
      orderId: props.supplierOrder?.orderId ?? '',
      body,
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('page.warehouse.notification.supplierOrderUpdated'));
        props.onSetReadonly(true);
      })
      .catch(handleApiError);
  };

  const getOptionsFromSuppliersByWarehouse = (suppliers: BaseSupplier[] | Nullish) => {
    if (isNilOrEmpty(suppliers)) {
      return [];
    }

    const filteredSuppliers = suppliers?.filter((supplier) => {
      const warehouseSuppliers = selectedWarehouse?.suppliers?.map(
        (warehouseSupplier) => warehouseSupplier?.id
      );
      return warehouseSuppliers?.includes(supplier.id);
    });

    return getOptionsFromSuppliers(filteredSuppliers);
  };

  return (
    <Form<SupplierOrderForm>
      experimentalZodSchema={SupplierOrderFormSchema}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
    >
      {(control, formApi) => {
        const warehouseId = formApi.watch('warehouseId');

        const handleWarehouseChange = (newWarehouseId: string | string[] | number | null) => {
          getWarehouse({warehouseId: newWarehouseId as string})
            .unwrap()
            .then((warehouse) => {
              setSelectedWarehouse(warehouse);
              formApi.setValue('supplierId', warehouse.defaultSupplierId, {shouldValidate: true});
            })
            .catch(handleApiError);
        };

        return (
          <>
            <VStack spacing={4}>
              <Grid columns={4}>
                <FormField
                  isRequired
                  isNotClearable
                  control={control}
                  onChange={handleWarehouseChange}
                  type="choice"
                  name="warehouseId"
                  label={i18n.t('entity.warehouse.labels.warehouse')}
                  options={getOptionsFromWarehouses(props.warehouses)}
                  data-testid={suffixTestId('inputs.warehouseId', props)}
                />
                <FormField
                  isRequired
                  isDisabled
                  isNotClearable
                  control={control}
                  type="choice"
                  name="orderVariantId"
                  label={i18n.t('entity.warehouse.labels.supplierOrderVariant')}
                  options={getOptionsFromSupplierOrderVariant(props.supplierOrderVariants)}
                  data-testid={suffixTestId('inputs.orderVariantId', props)}
                />
                <FormField
                  isRequired
                  isNotClearable
                  isDisabled={isNil(warehouseId) || isWarehouseFetching}
                  isLoading={isWarehouseFetching}
                  control={control}
                  type="choice"
                  name="supplierId"
                  label={i18n.t('entity.warehouse.labels.supplier')}
                  options={getOptionsFromSuppliersByWarehouse(props.suppliers)}
                  data-testid={suffixTestId('inputs.supplierId', props)}
                />
                <FormField
                  control={control}
                  type="choice"
                  name="type"
                  label={i18n.t('entity.warehouse.labels.supplierOrderType')}
                  options={props.supplierOrderTypes || []}
                  data-testid={suffixTestId('inputs.type', props)}
                />
                <FormField
                  isNotClearable
                  control={control}
                  type="choice"
                  name="supplierAccountCode"
                  label={i18n.t('entity.warehouse.labels.supplierAccount')}
                  options={props.supplierAccounts || []}
                  isDisabled={(props.supplierAccounts?.length ?? 0) <= 1}
                  data-testid={suffixTestId('inputs.supplierAccountCode', props)}
                />
              </Grid>
              <Grid columns={4}>
                <FormField
                  control={control}
                  type="choice"
                  name="currency"
                  label={i18n.t('entity.warehouse.labels.currency')}
                  options={getOptionsFromCurrencies(props.currencies)}
                  data-testid={suffixTestId('inputs.currency', props)}
                />
                <FormField
                  control={control}
                  type="number"
                  name="exchangeRate"
                  label={i18n.t('entity.warehouse.labels.exchangeRate')}
                  data-testid={suffixTestId('inputs.exchangeRate', props)}
                />
                <FormField
                  control={control}
                  type="choice"
                  name="ncConversion"
                  label={i18n.t('entity.warehouse.labels.ncConversion')}
                  options={[]}
                  isDisabled
                  data-testid={suffixTestId('inputs.ncConversion', props)}
                />
              </Grid>
              <Grid columns={4}>
                <GridItem span={4}>
                  <FormField
                    control={control}
                    type="textarea"
                    name="note"
                    label={i18n.t('entity.warehouse.labels.note')}
                    data-testid={suffixTestId('inputs.note', props)}
                  />
                </GridItem>
              </Grid>
            </VStack>
            <Separator />
            <HStack justify="flex-end">
              <ButtonGroup>
                <FormButton
                  control={control}
                  type="reset"
                  variant="secondary"
                  title={i18n.t('general.labels.discard')}
                  onClick={handleDiscard}
                  data-testid={suffixTestId('actions.discard', props)}
                />
                <FormButton
                  control={control}
                  type="submit"
                  variant="primary"
                  title={i18n.t('general.labels.saveChanges')}
                  data-testid={suffixTestId('actions.submit', props)}
                />
              </ButtonGroup>
            </HStack>
          </>
        );
      }}
    </Form>
  );
}
